import { v4 as uuidv4 } from 'uuid';

// ログレベルの定義
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
  TRACE: 4
};

// 環境変数からログレベルを取得（デフォルトはINFO）
const getLogLevel = () => {
  const envLevel = process.env.REACT_APP_LOG_LEVEL;
  if (envLevel && LOG_LEVELS.hasOwnProperty(envLevel.toUpperCase())) {
    return LOG_LEVELS[envLevel.toUpperCase()];
  }
  return LOG_LEVELS.INFO; // デフォルトレベル
};

// デバッグモードの判定
const isDebugMode = () => {
  return process.env.NODE_ENV === 'development' || 
         window.location.search.includes('debug=true');
};

// タイムスタンプの生成
const getTimestamp = () => {
  return new Date().toISOString();
};

// リクエストIDの生成
const generateRequestId = () => {
  return uuidv4().substring(0, 8);
};

/**
 * 構造化ロガークラス
 */
class Logger {
  constructor() {
    this.level = getLogLevel();
    this.debugMode = isDebugMode();
  }

  /**
   * ログレベルを設定
   * @param {string} level - ログレベル名
   */
  setLevel(level) {
    if (LOG_LEVELS.hasOwnProperty(level.toUpperCase())) {
      this.level = LOG_LEVELS[level.toUpperCase()];
    }
  }

  /**
   * エラーログを出力
   * @param {string} category - ログカテゴリ
   * @param {string} message - ログメッセージ
   * @param {Object} data - 追加データ
   */
  error(category, message, data = {}) {
    if (this.level >= LOG_LEVELS.ERROR) {
      console.error(`[ERROR][${category}] ${message}`, {
        ...data,
        timestamp: getTimestamp()
      });
    }
  }

  /**
   * 警告ログを出力
   * @param {string} category - ログカテゴリ
   * @param {string} message - ログメッセージ
   * @param {Object} data - 追加データ
   */
  warn(category, message, data = {}) {
    if (this.level >= LOG_LEVELS.WARN) {
      console.warn(`[WARN][${category}] ${message}`, {
        ...data,
        timestamp: getTimestamp()
      });
    }
  }

  /**
   * 情報ログを出力
   * @param {string} category - ログカテゴリ
   * @param {string} message - ログメッセージ
   * @param {Object} data - 追加データ
   */
  info(category, message, data = {}) {
    if (this.level >= LOG_LEVELS.INFO) {
      console.log(`[INFO][${category}] ${message}`, {
        ...data,
        timestamp: getTimestamp()
      });
    }
  }

  /**
   * デバッグログを出力
   * @param {string} category - ログカテゴリ
   * @param {string} message - ログメッセージ
   * @param {Object} data - 追加データ
   */
  debug(category, message, data = {}) {
    if (this.level >= LOG_LEVELS.DEBUG) {
      console.log(`[DEBUG][${category}] ${message}`, {
        ...data,
        timestamp: getTimestamp()
      });
    }
  }

  /**
   * トレースログを出力（詳細なデバッグ情報）
   * @param {string} category - ログカテゴリ
   * @param {string} message - ログメッセージ
   * @param {Object} data - 追加データ
   */
  trace(category, message, data = {}) {
    if (this.level >= LOG_LEVELS.TRACE) {
      console.log(`[TRACE][${category}] ${message}`, {
        ...data,
        timestamp: getTimestamp()
      });
    }
  }

  /**
   * 画像関連のログを出力する専用メソッド
   * @param {string} level - ログレベル（error, warn, info, debug, trace）
   * @param {string} component - コンポーネント名
   * @param {string} message - ログメッセージ
   * @param {Object} imageData - 画像データ
   */
  imageLog(level, component, message, imageData = {}) {
    // 画像URLの短縮表示用
    const truncateUrl = (url) => {
      if (!url) return 'undefined';
      if (typeof url !== 'string') return String(url);
      return url.length > 50 ? url.substring(0, 47) + '...' : url;
    };

    // nullやundefinedを安全に処理
    if (imageData === null || imageData === undefined) {
      imageData = {};
    }

    // 画像データを安全に加工
    const safeImageData = {
      ...imageData,
      url: imageData.url ? truncateUrl(imageData.url) : undefined,
      localPath: imageData.localPath ? truncateUrl(imageData.localPath) : undefined,
      base64: imageData.base64 ? `${imageData.base64.substring(0, 20)}...` : undefined,
      timestamp: getTimestamp()
    };

    // レベルに応じたメソッドを呼び出し
    switch (level.toLowerCase()) {
      case 'error':
        this.error('Image', `[${component}] ${message}`, safeImageData);
        break;
      case 'warn':
        this.warn('Image', `[${component}] ${message}`, safeImageData);
        break;
      case 'info':
        this.info('Image', `[${component}] ${message}`, safeImageData);
        break;
      case 'debug':
        this.debug('Image', `[${component}] ${message}`, safeImageData);
        break;
      case 'trace':
        this.trace('Image', `[${component}] ${message}`, safeImageData);
        break;
      default:
        this.info('Image', `[${component}] ${message}`, safeImageData);
    }
  }

  generateRequestId() {
    return generateRequestId();
  }
}

// ロガーのシングルトンインスタンス
export const logger = new Logger();

// 簡易デバッグログ関数 - 下位互換性のため維持
export const debugLog = (message, data = {}) => {
  if (isDebugMode()) {
    console.log(`[DEBUG] ${message}`, {
      ...data,
      timestamp: getTimestamp()
    });
  }
};

export default logger; 